import { OtpFlowKey } from 'features/otp/constants/OtpFlowKey';
import { OtpVerificationType } from 'features/otp/constants/OtpVerificationType';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Keyboard, View } from 'react-native';
import Button from 'shared/components/vxl/Button/Button';

interface ResendOtpButtonProps {
  flowKey?: OtpFlowKey;
  verificationType?: OtpVerificationType;
  onPress: () => void;
  onExpiry?: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  resendCodeAttempt?: number;
  initialCountdown?: number;
  analyticsId?: string;
  isCode?: boolean;
}

const ResendOtpButton = ({
  verificationType,
  onPress,
  onExpiry,
  isLoading = false,
  isDisabled = false,
  resendCodeAttempt = 1,
  initialCountdown = 20,
  analyticsId = 'resendClicked',
  isCode = false,
}: ResendOtpButtonProps) => {
  const { t: translatedText } = useTranslation('otp');

  const [countdown, setCountdown] = useState(initialCountdown);
  const handlePress = () => {
    Keyboard.dismiss();

    onPress();
  };

  const onExpiryRef = useRef(onExpiry);
  useEffect(() => {
    onExpiryRef.current = onExpiry;
  }, [onExpiry]);

  // Ref to track previous loading state
  const prevIsLoadingRef = useRef(isLoading);

  // Countdown timer effect
  useEffect(() => {
    // Reset countdown when isLoading changes from true to false (request completed)
    const justFinishedLoading =
      prevIsLoadingRef.current === true && isLoading === false;
    if (justFinishedLoading) {
      setCountdown(initialCountdown);
    }
    prevIsLoadingRef.current = isLoading;

    const intervalId = setInterval(() => {
      setCountdown((prevCount) => {
        if (prevCount <= 1) {
          clearInterval(intervalId);
          return 0;
        }
        return prevCount - 1;
      });
    }, 1000);

    // Clean up the interval
    return () => clearInterval(intervalId);
  }, [isLoading, initialCountdown]);

  const hasExpiredRef = useRef(false);
  useEffect(() => {
    if (countdown > 0) {
      hasExpiredRef.current = false;
      return;
    }
    if (!hasExpiredRef.current) {
      hasExpiredRef.current = true;
      onExpiryRef.current?.();
    }
  }, [countdown]);

  const getButtonLabel = () => {
    if (verificationType === OtpVerificationType.PHONE) {
      if (countdown > 0) {
        return `${translatedText('buttons.resendCodeWaitMessage')} ${countdown}`;
      }
      return translatedText('buttons.resendCode');
    }
    if (countdown > 0) {
      return `${isCode ? translatedText('buttons.resendCodeWaitMessage') : translatedText('buttons.resendLinkWaitMessage')} ${countdown}`;
    }
    return isCode
      ? translatedText('buttons.resendCode')
      : translatedText('buttons.resendLink');
  };

  return (
    <View>
      <Button
        label={getButtonLabel()}
        type="TEXT"
        isDisabled={isDisabled || countdown > 0}
        onPress={handlePress}
        size="LARGE"
        width="FULL"
        analyticsId={analyticsId}
        customAttributes={{
          verification_type:
            verificationType === OtpVerificationType.PHONE ? 'phone' : 'email',
          verification_attempt: resendCodeAttempt,
        }}
        isLoading={isLoading}
      />
    </View>
  );
};

export default ResendOtpButton;
